/**
 * Lightweight WYSIWYG editor built on contentEditable + document.execCommand.
 * Chosen to avoid pulling a new dependency; sufficient for CMS standard
 * pages (About / Contact / FAQ / Terms / Privacy). Output is plain HTML.
 *
 * Extensible: pass extra toolbar buttons via `extraCommands` in the future.
 */
import React, { useEffect, useRef } from "react";
import DOMPurify from "dompurify";
import {
  TextB, TextItalic, TextUnderline, TextStrikethrough,
  TextHOne, TextHTwo, TextHThree,
  ListBullets, ListNumbers, LinkSimple, Quotes, Code, ArrowUUpLeft, ArrowUUpRight,
} from "@phosphor-icons/react";

const BUTTONS = [
  { cmd: "bold",         icon: TextB,             title: "Bold (Ctrl+B)" },
  { cmd: "italic",       icon: TextItalic,        title: "Italic (Ctrl+I)" },
  { cmd: "underline",    icon: TextUnderline,     title: "Underline (Ctrl+U)" },
  { cmd: "strikeThrough",icon: TextStrikethrough, title: "Strikethrough" },
  null,
  { block: "H1", icon: TextHOne,   title: "Heading 1" },
  { block: "H2", icon: TextHTwo,   title: "Heading 2" },
  { block: "H3", icon: TextHThree, title: "Heading 3" },
  null,
  { cmd: "insertUnorderedList", icon: ListBullets, title: "Bulleted list" },
  { cmd: "insertOrderedList",   icon: ListNumbers, title: "Numbered list" },
  { block: "BLOCKQUOTE", icon: Quotes, title: "Quote" },
  { block: "PRE",        icon: Code,   title: "Code block" },
  null,
  { link: true, icon: LinkSimple, title: "Insert link" },
  null,
  { cmd: "undo", icon: ArrowUUpLeft,  title: "Undo" },
  { cmd: "redo", icon: ArrowUUpRight, title: "Redo" },
];

export default function RichTextEditor({ value, onChange, testid = "rich-editor" }) {
  const ref = useRef(null);

  // Sync incoming value → contentEditable innerHTML (only when it actually
  // differs, so caret position isn't reset on every keystroke). Sanitise on
  // the way in so a stored draft carrying a stray <script> or on* handler
  // (from a paste, an older editor version, or a hostile importer) can never
  // execute when rehydrated.
  useEffect(() => {
    if (!ref.current) return;
    const clean = DOMPurify.sanitize(value || "", { USE_PROFILES: { html: true } });
    if (clean !== ref.current.innerHTML) {
      ref.current.innerHTML = clean;
    }
  }, [value]);

  const exec = (cmd, arg) => {
    ref.current?.focus();
    document.execCommand(cmd, false, arg);
    onChange(ref.current?.innerHTML || "");
  };
  const applyBlock = (tag) => exec("formatBlock", tag);
  const insertLink = () => {
    const url = window.prompt("Link URL:", "https://");
    if (url) exec("createLink", url);
  };
  const onInput = () => onChange(ref.current?.innerHTML || "");

  return (
    <div className="border border-border rounded-md" data-testid={testid}>
      <div className="flex flex-wrap items-center gap-0.5 p-1.5 border-b border-border bg-secondary/40 rounded-t-md">
        {BUTTONS.map((b, i) => {
          if (b === null) return <div key={i} className="w-px h-5 bg-border mx-1" />;
          const Ic = b.icon;
          const onClick = () => {
            if (b.link) return insertLink();
            if (b.block) return applyBlock(b.block);
            if (b.cmd) return exec(b.cmd);
          };
          return (
            <button key={i} type="button" onMouseDown={(e) => e.preventDefault()} onClick={onClick}
              className="p-1.5 rounded hover:bg-secondary text-muted-foreground hover:text-foreground"
              title={b.title} data-testid={`rt-${b.cmd || b.block || (b.link && "link")}`}>
              <Ic size={14} weight="duotone" />
            </button>
          );
        })}
      </div>
      <div
        ref={ref}
        contentEditable
        suppressContentEditableWarning
        onInput={onInput}
        className="min-h-[300px] p-4 text-sm leading-relaxed focus:outline-none prose prose-sm max-w-none dark:prose-invert"
        data-testid={`${testid}-area`}
      />
    </div>
  );
}
